import { Link } from "react-router-dom";

import {
  Grid,
  Box,
  Card,
  Button,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from "@mui/material";

import CallSplitIcon from "@mui/icons-material/CallSplit";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import MultilineChartIcon from "@mui/icons-material/MultilineChart";
import AddRoadIcon from "@mui/icons-material/AddRoad";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";

import TexasRoads from "../../images/texas_roads.jpg";
import ScrollTop from "./ScrollTop";

const sectionStyle = {
  display: "flex",
  alignContent: "center",
  margin: "25px",
};

const heroStyle = {
  display: "flex",
  alignContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  padding: "35px 25px",
};

const centeredButtonStyle = {
  display: "flex",
  justifyContent: "center",
};

const purposeItems = [
  { icon: AddLocationIcon, text: "Trip performance at select locations" },
  {
    icon: EmojiTransportationIcon,
    text: "Freight performance for key corridors and commodities",
  },
  {
    icon: AllInclusiveIcon,
    text: "Transportation performance on Texas business supply chains",
  },
  { icon: AddRoadIcon, text: "Freight resiliency" },
  { icon: MultilineChartIcon, text: "Multimodal trip performance" },
];

const caseStudyApproaches = [
  {
    image: require("../../images/time-contours.PNG"),
    alt: "Travel time contour map",
    title: "Contour Maps",
    description:
      "show how far freight can travel at different times of day, which is useful for route planning.",
  },
  {
    image: require("../../images/heatmaps.PNG"),
    alt: "Truck density heat map",
    title: "Heat Maps",
    description:
      "show the density of trucks along routes in these locations, which is useful for understanding truck volumes, congestion, and key routes.",
  },
  {
    image: require("../../images/tt.PNG"),
    alt: "Travel time traces chart",
    title: "Travel Time Traces",
    description:
      "show how long a trip through these locations will take by time of day, which is useful for routing and trip planning.",
  },
  {
    image: require("../../images/tx100.PNG"),
    alt: "Texas 100 Most Congested Roadways bottleneck map",
    title: "Texas 100 Most Congested Roadways",
    description:
      "bottleneck mapping is useful for understanding the magnitude of bottlenecks based on the delay that freight users experience in these locations.",
  },
];

const SingleImage = ({ src, alt, caption }) => (
  <ImageList sx={{ width: "auto", height: "auto" }}>
    <ImageListItem>
      <img src={src} alt={alt} />
    </ImageListItem>
    {caption && <ImageListItemBar title={caption} position="below" />}
  </ImageList>
);

const Home = () => {
  return (
    <div className="homelayout">
      {/* Hero section */}
      <div
        className="homegrid1"
        style={{
          backgroundImage: `url(${TexasRoads})`,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box sx={heroStyle}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <h3 className="homeheading">Freight Fluidity Visualization Tool</h3>
              <h4 className="homesubheading" style={{ marginBottom: 0, paddingBottom: 0 }}>
                A Tool for Decision-Making
              </h4>
            </Grid>

            <Grid item xs={6} style={{ borderRight: "1px solid white" }}>
              <div className="homedescription" style={{ marginTop: 0, paddingTop: 0 }}>
                Freight fluidity measures trip performance to determine how
                efficiently goods are moving through a region. It involves
                answering questions such as:
                <div>
                  <WarehouseIcon fontSize="large" /> What are the goods?
                </div>
                <div>
                  <MoveDownIcon fontSize="large" /> How do they get from point A
                  to point B?
                </div>
                <div>
                  <CallSplitIcon fontSize="large" /> What is the route?
                </div>
              </div>
            </Grid>

            <Grid item xs={6}>
              <p className="homedescription" style={{ marginTop: 0, paddingTop: 0 }}>
                The tool consists of pages that display freight fluidity
                information and allow users to interact with the data. Each
                page can be accessed from the app bar at the top of the screen
                and is briefly described below. Users can return to this page
                at any time by clicking the "Freight Fluidity" button in the
                app bar.
              </p>
            </Grid>
          </Grid>
        </Box>
      </div>

      {/* Purpose */}
      <div className="homegrid2">
        <Box sx={sectionStyle}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card variant="text">
                <SingleImage
                  src={require("../../images/DSC_3097.jpg")}
                  alt="Freight trucks on a Texas highway"
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card variant="text">
                <h3 className="homeheading">Purpose</h3>
                <hr />
                <div className="homedescription">
                  The purpose of this freight fluidity visualization tool is to
                  provide an interactive environment where users can explore the
                  different types of information that help tell the freight
                  fluidity story. This tool provides a snapshot of fluidity
                  information for:
                  {purposeItems.map(({ icon: Icon, text }) => (
                    <div key={text}>
                      <Icon sx={{ fontSize: "40px" }} /> {text}
                    </div>
                  ))}
                </div>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </div>

      {/* Location-specific case studies */}
      <div className="homegrid3">
        <Box sx={sectionStyle}>
          <Grid container>
            <Grid item xs={12}>
              <Card variant="text" sx={{ textAlign: "center" }}>
                <h3 className="homeheading">Location-Specific Case Studies</h3>
                <hr />
                <p className="homedescription">
                  Freight fluidity analysis can help decision-makers understand
                  freight movement and how to manage it more efficiently at the
                  local, state, regional, and national levels. The Case Studies
                  page presents case studies of three freight-significant
                  locations, meaning places in Texas with heavy freight
                  movement. The visualizations show four different ways these
                  locations were assessed to understand the business user
                  experience and to provide context for freight trips through
                  these locations. The four approaches used to assess how well
                  freight moves through these locations include:
                </p>
              </Card>
            </Grid>

            {caseStudyApproaches.map(({ image, alt }, index) => (
              <Grid item xs={3} key={alt} className={index === 0 ? "imagelist" : undefined}>
                <Card variant="text">
                  <SingleImage src={image} alt={alt} />
                </Card>
              </Grid>
            ))}

            {caseStudyApproaches.map(({ title, description }) => (
              <Grid item xs={3} key={title}>
                <p className="homesubdescription">
                  <span>{title}</span> {description}
                </p>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Card variant="text" sx={{ textAlign: "center" }}>
                <p className="homedescription">
                  The location-specific case studies can be accessed from the
                  "Case Studies" dropdown menu in the app bar at the top of the
                  screen or by clicking the button below.
                </p>
              </Card>
            </Grid>

            <Grid item xs={12} sx={{ ...centeredButtonStyle, mb: "25px" }}>
              <Button
                component={Link}
                to="/case-studies/?location=DFW"
                variant="contained"
                className="vizbutton"
                size="large"
                sx={{ color: "black" }}
              >
                Explore Location-Specific Case Studies
              </Button>
            </Grid>
          </Grid>
        </Box>
      </div>

      {/* Commodity corridors */}
      <Box sx={sectionStyle}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card variant="text">
              <h3 className="homeheading">Commodity Corridors</h3>
              <hr />
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card variant="text">
              <SingleImage
                src={require("../../images/CommodityCorridorsLandingPage.PNG")}
                alt="Commodity Corridors page preview"
                caption="Commodity Corridors reliability metrics overview"
              />
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card variant="text">
              <p className="homedescription">
                Freight fluidity analysis helps illustrate why improvements are
                critical to moving goods efficiently and helps justify the cost
                of those improvements. It can also demonstrate how proposed
                improvements may affect the surrounding transportation network.
              </p>
              <p className="homedescription">
                The Commodity Corridors page shows relative commodity flows
                alongside roadway performance in Texas. This provides a visual
                representation of the user experience for commodities on these
                corridors and highlights where the most critical bottlenecks
                are. This information can help identify where investments in
                capacity or technology are needed to improve efficiency for
                specific commodities and their routes.
              </p>
            </Card>
          </Grid>

          <Grid item xs={12} sx={centeredButtonStyle}>
            <Button
              component={Link}
              to="/commodity-corridors"
              variant="contained"
              className="vizbutton"
              size="large"
              sx={{ color: "black" }}
            >
              Explore Commodity Corridors
            </Button>
          </Grid>
        </Grid>
      </Box>

      <ScrollTop />
    </div>
  );
};

export default Home;